import { PageHelp } from '@/help/HelpCenter';
import SessionQueryTrace from '@/components/monitoring/SessionQueryTrace';
import { captureQueryTraceAction, setQueryTraceRecording, getQueryTraceRecording, subscribeQueryTraceRecording, isQueryTraceSupported, subscribeQueryTraceSupport } from '@/lib/queryTrace';
import api from '@/api/client';
import MobileNavigation from '@/components/navigation/MobileNavigation';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import {
  Menu,
  BookOpen,
  LogOut,
  LayoutDashboard,
  Cpu,
  Building2,
  Bell,
  ListTodo,
  ThermometerSun,
  ChartColumn,
  Pin,
  PinOff,
  SquareKanban,
  Layers,
  Wrench,
  Search,
  X,
  Settings,
  Activity,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import AISearchBar from '@/components/AISearch/AISearchBar';
import GlobalSearch from '@/components/GlobalSearch/GlobalSearch';
import { useAuthStore } from '@/store/authStore';
import { useI18n } from '@/i18n';
import { fetchNewActiveAlarmsLastHour, type NewLastHourAlarm, type NewLastHourAlarmResponse } from '@/api/alarms';
import brandIcon from '@/assets/ges-master-nav-icon.png';
import { cn } from '@/lib/utils';
import type { PageTitle, DashboardOutletContext } from './usePageTitle';

export default function DashboardLayout() {
  const { t } = useI18n();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const queryTraceSupported = useSyncExternalStore(subscribeQueryTraceSupport, isQueryTraceSupported);
  const queryTraceRecording = useSyncExternalStore(subscribeQueryTraceRecording, getQueryTraceRecording) && queryTraceSupported;
  const [queryTraceOpen, setQueryTraceOpen] = useState(false);
  const closeQueryTrace = useCallback(() => setQueryTraceOpen(false), []);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [pinned, setPinned] = useState(() => localStorage.getItem('sidebar-pinned') === 'true');
  const buildDateLabel = new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(__APP_BUILD_DATE__));

  function handleLogout() {
    logout();
    navigate('/login');
  }

  function togglePin() {
    setPinned(p => {
      localStorage.setItem('sidebar-pinned', String(!p));
      return !p;
    });
  }

  const labelCls = pinned
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100 transition-opacity duration-200 delay-100';

  const location = useLocation();
  const [pageTitle, setPageTitle] = useState<PageTitle>(null);
  const [headerActionsTarget, setHeaderActionsTarget] = useState<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newAlarmCount, setNewAlarmCount] = useState(0);
  const [newAlarmRows, setNewAlarmRows] = useState<NewLastHourAlarm[]>([]);
  const [alarmSummary, setAlarmSummary] = useState<NewLastHourAlarmResponse['summary']>();
  const [alarmLoadError, setAlarmLoadError] = useState(false);
  const [alarmMenuOpen, setAlarmMenuOpen] = useState(false);
  const alarmMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!queryTraceSupported) { setQueryTraceRecording(false); setQueryTraceOpen(false); return; }
    let active = true;
    const refreshTraceState = () => { void api.get('/monitoring/session-trace/status').then(({ data }) => { if (active) setQueryTraceRecording(data.enabled); }).catch(() => {}); };
    refreshTraceState();
    const timer = window.setInterval(refreshTraceState, 30000);
    document.addEventListener('click', captureClick, true);
    function captureClick(event: MouseEvent) { captureQueryTraceAction(event.target); }
    return () => { active = false; window.clearInterval(timer); setQueryTraceRecording(false); document.removeEventListener('click', captureClick, true); };
  }, [queryTraceSupported]);

  const navigationItems: Array<{
    to: string;
    icon: ReactNode;
    label: string;
    category: string;
    isExternal?: boolean;
  }> = [
    { to: "/dashboard",             icon: <LayoutDashboard className="h-5 w-5" />, label: t('layout.nav.home'),        category: 'Menu' },
    { to: "/dashboard/powerplants", icon: <Building2 className="h-5 w-5" />,       label: t('layout.nav.powerPlants'), category: 'Menu' },
    { to: "/dashboard/sites",       icon: <Layers className="h-5 w-5" />,          label: t('layout.nav.sites'),       category: 'Menu' },
    { to: "/dashboard/inverters",   icon: <Cpu className="h-5 w-5" />,             label: t('layout.nav.inverters'),   category: 'Menu' },

    { to: "/dashboard/sensors",     icon: <ThermometerSun className="h-5 w-5" />,  label: t('layout.nav.sensors'),     category: 'Yönetim' },
    { to: "/dashboard/investor",    icon: <Building2 className="h-5 w-5" />,       label: t('layout.nav.investors'),   category: 'Yönetim' },
    { to: "/dashboard/maintenance", icon: <Wrench className="h-5 w-5" />,         label: t('layout.nav.maintenance'), category: 'Yönetim' },
    { to: "/dashboard/alarms",      icon: <Bell className="h-5 w-5" />,            label: t('layout.nav.alarms'),      category: 'Yönetim' },

    { to: "/dashboard/tasks",       icon: <ListTodo className="h-5 w-5" />,        label: t('layout.nav.tasks'),       category: 'Task & Agile' },
    { to: "/dashboard/agile",       icon: <SquareKanban className="h-5 w-5" />,    label: t('layout.nav.agile'),       category: 'Task & Agile' },

    { to: "/dashboard/reports",     icon: <ChartColumn className="h-5 w-5" />,     label: t('layout.nav.reports'),     category: 'Sistem' },
    { to: "/dashboard/automation",  icon: <Layers className="h-5 w-5" />,        label: t('layout.nav.automation'),  category: 'Sistem' },
    { to: "/dashboard/management", icon: <Settings className="h-5 w-5" />,   label: t('layout.nav.management'),  category: 'Sistem' },
    { to: "/dashboard/help", icon: <BookOpen className="h-5 w-5" />, label: "Keşfet ve Yardım", category: "Sistem" },
    { to: "/dashboard/monitoring",  icon: <Activity className="h-5 w-5" />,        label: t('layout.nav.monitoring'),  category: 'Sistem' },
  ];

  const currentPage = navigationItems.find(item => {
    if (item.to === location.pathname) return true;
    if (location.pathname.startsWith(item.to + '/') && item.to !== '/dashboard') return true;
    return false;
  });
  useEffect(() => { setMobileNavigationOpen(false); }, [location.pathname]);
  const mobileItems = navigationItems
    .filter(item => item.to !== '/dashboard/help')
    .map(item => ({ key: item.to, label: item.label, icon: item.icon }));
  const currentTitle = (pageTitle?.pathname === location.pathname ? pageTitle.title : null) || currentPage?.label || 'İstasyon İzleme';
  const separateListActions = ['/dashboard/powerplants', '/dashboard/sites', '/dashboard/inverters', '/dashboard/sensors', '/dashboard/investor'].includes(location.pathname);
  const fullHeightList = ['/dashboard/powerplants','/dashboard/sites','/dashboard/inverters','/dashboard/investor','/dashboard/sensors','/dashboard/alarms','/dashboard/maintenance','/dashboard/tasks','/dashboard/agile'].includes(location.pathname) || /^\/dashboard\/powerplants\/[^/]+\/inverters$/.test(location.pathname);
  const pageIconColors: Record<string, string> = {
    '/dashboard/powerplants': 'bg-amber-50 text-amber-600',
    '/dashboard/sites': 'bg-emerald-50 text-emerald-600',
    '/dashboard/inverters': 'bg-blue-50 text-blue-600',
    '/dashboard/investor': 'bg-rose-50 text-rose-600',
    '/dashboard/sensors': 'bg-cyan-50 text-cyan-600',
    '/dashboard/alarms': 'bg-red-50 text-red-600',
    '/dashboard/tasks': 'bg-lime-50 text-lime-700',
    '/dashboard/maintenance': 'bg-orange-50 text-orange-600',
    '/dashboard/agile': 'bg-indigo-50 text-indigo-600',
    '/dashboard/reports': 'bg-teal-50 text-teal-600',
    '/dashboard/monitoring': 'bg-fuchsia-50 text-fuchsia-600',
    '/dashboard/management': 'bg-violet-50 text-violet-600',
  };

  const filteredItems = navigationItems.filter(item => {
    const query = searchQuery.toLocaleLowerCase('tr-TR');
    return (
      item.label.toLocaleLowerCase('tr-TR').includes(query) ||
      item.category.toLocaleLowerCase('tr-TR').includes(query)
    );
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await fetchNewActiveAlarmsLastHour();
        if (!cancelled) {
          setNewAlarmCount(Number(data.count) || 0);
          setNewAlarmRows(data.rows || []);
          setAlarmSummary(data.summary);
          setAlarmLoadError(false);
        }
      } catch {
        if (!cancelled) {
          setAlarmLoadError(true);
        }
      }
    };

    void load();
    const timer = window.setInterval(() => {
      void load();
    }, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!alarmMenuRef.current) return;
      if (!alarmMenuRef.current.contains(event.target as Node)) {
        setAlarmMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-[#D1D1D1]/20 font-sans p-1 gap-1 sm:p-2 sm:gap-2 xl:p-4 xl:gap-4">

      {queryTraceSupported && queryTraceOpen && <SessionQueryTrace onClose={closeQueryTrace} />}
      <MobileNavigation open={mobileNavigationOpen} onClose={() => setMobileNavigationOpen(false)} items={mobileItems} selected={currentPage?.to ?? ''} onSelect={to => navigate(to)} userName={user?.name || user?.email || ''} onLogout={handleLogout} />
      {/* Sidebar */}
      <div className={`relative hidden xl:block flex-shrink-0 transition-[width] duration-300 ease-in-out ${pinned ? 'w-64' : 'w-20'}`}>
        <aside className={`
          ${pinned ? '' : 'group'}
          absolute inset-y-0 left-0 z-30
          flex flex-col
          bg-[#FCFCFC] border border-white shadow-[0px_4px_32px_rgba(0,0,0,0.05)]
          rounded-[32px]
          ${pinned ? 'w-64' : 'w-20 hover:w-64'}
          transition-[width] duration-300 ease-in-out
          overflow-hidden
        `}>
          {/* Logo Section */}
          <div className="flex items-center gap-3 px-5 py-6 flex-shrink-0">
            <div className="h-10 w-10 flex-shrink-0 bg-white border border-slate-100 rounded-full flex items-center justify-center shadow-sm transition-transform group-hover:scale-105 overflow-hidden p-1.5">
              <img
                src={brandIcon}
                alt="GES Master"
                className="h-full w-full object-cover rounded-full"
              />
            </div>
            <div className={`flex-1 leading-tight overflow-hidden whitespace-nowrap ${labelCls}`}>
              <div className="font-black text-slate-800 text-[13px] tracking-tight">GES Master</div>
              <div className="text-[10px] font-bold text-slate-400 tracking-wider uppercase opacity-80">{t('layout.portal')}</div>
            </div>
          </div>

          <div className="px-4 mb-2">
            <button
              type="button"
              onClick={togglePin}
              aria-label={pinned ? 'Menü sabitlemesini kaldır' : 'Menüyü açık tut'}
              aria-pressed={pinned}
              title={pinned ? 'Menü sabitlemesini kaldır' : 'Menüyü açık tut'}
              className={cn(
                'flex h-10 w-full items-center gap-3 overflow-hidden rounded-xl px-3 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500',
                pinned ? 'bg-blue-50 text-blue-700' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700',
              )}
            >
              {pinned ? <PinOff size={16} className="shrink-0" /> : <Pin size={16} className="shrink-0" />}
              <span className={cn('whitespace-nowrap text-xs font-semibold', labelCls)}>
                {pinned ? 'Sabitlemeyi kaldır' : 'Menüyü açık tut'}
              </span>
            </button>
          </div>

          <div className="hidden">
             <div className={cn("flex items-center gap-2 bg-slate-100/50 border border-slate-100 rounded-2xl px-3 py-2", !pinned && "justify-center border-transparent transition-all")}>
                <Search size={16} className="text-slate-400 shrink-0" />
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Ara..." 
                  className={cn("bg-transparent border-none outline-none text-xs font-semibold text-slate-700 placeholder:text-slate-400 w-full", !pinned && "hidden group-hover:block transition-all")} 
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="p-0.5 hover:bg-slate-200 rounded-full text-slate-400">
                    <X size={12} />
                  </button>
                )}
             </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-1 space-y-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
            {filteredItems.map(item => (
              item.isExternal ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100/80 hover:text-slate-900 transition-all duration-200"
                >
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">{item.icon}</span>
                  <span className={cn("whitespace-nowrap overflow-hidden transition-all duration-300", labelCls)}>
                    {item.label}
                  </span>
                </a>
              ) : (
                <SideNavLink key={item.to} {...item} labelCls={labelCls} />
              )
            ))}
            
            {filteredItems.length === 0 && (
              <div className="px-4 py-8 text-center">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sonuç Yok</div>
                <p className="text-[10px] text-slate-400 mt-1 italic">Başka bir şey deneyin</p>
              </div>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 flex-shrink-0">
            <div className="min-h-[64px]">
              {/* Profile Row: Avatar is flush left */}
              <div className="flex items-center h-12 px-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-white font-black border-2 border-white shadow-md shrink-0">
                    <span className="leading-none text-xs">{user?.name?.charAt(0).toUpperCase() ?? 'U'}</span>
                  </div>
                </div>
                <div className={cn("ml-5 leading-tight overflow-hidden flex-1 min-w-0 transition-all duration-300", labelCls)}>
                  <div className="text-[13px] font-black text-slate-900 truncate tracking-tight">{user?.name || 'Admin'}</div>
                  <div className="text-[10px] font-bold text-slate-400 truncate tracking-tight">{user?.email}</div>
                </div>
              </div>
              
              {/* Logout Button: Identical to SideNavLink px-3 structure */}
              <button
                onClick={handleLogout}
                className="mt-2 flex items-center h-12 rounded-2xl px-3 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100/80 hover:text-rose-500 transition-all border border-transparent w-full"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <LogOut className="h-5 w-5" />
                </div>
                <span className={cn("ml-3 whitespace-nowrap overflow-hidden transition-all duration-300", labelCls)}>
                  Oturumu Kapat
                </span>
              </button>

              <div className={cn("mt-2 px-3 overflow-hidden whitespace-nowrap transition-all duration-300", labelCls)}>
                <div className="flex items-center gap-2 text-[10px] text-slate-400">
                  <span>v{__APP_VERSION__}</span>
                  <span className="text-slate-300">•</span>
                  <span>{buildDateLabel}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col min-w-0 bg-white shadow-[0px_4px_32px_rgba(0,0,0,0.03)] rounded-[32px] border border-white overflow-hidden relative">
        {/* Header (Integrated into content card) */}
        <header className="flex min-h-16 flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 sm:px-8 border-b border-slate-50 flex-shrink-0">
          <div className={cn("flex min-w-0 flex-1 items-center gap-3", fullHeightList && (separateListActions ? "xl:flex-none" : "sm:flex-none"))}>
            <button type="button" aria-label="Menüyü aç" aria-controls="main-mobile-navigation" aria-expanded={mobileNavigationOpen} onClick={() => { setAlarmMenuOpen(false); setMobileNavigationOpen(true); }} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 xl:hidden"><Menu size={20} /></button>
            {currentPage && (
              <span aria-hidden="true" className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', pageIconColors[currentPage.to] || 'bg-slate-100 text-slate-600')}>
                {currentPage.icon}
              </span>
            )}
             <div className="flex-1 min-w-0">
              <h2 title={currentTitle} className="truncate text-xl font-black text-slate-900 tracking-tight transition-all duration-300">
                {currentTitle}
              </h2>
            </div>
          </div>
          
          <div ref={setHeaderActionsTarget} className={cn("order-3 flex w-full min-w-0 items-center gap-2 empty:hidden", separateListActions ? "justify-start border-t border-slate-100 pt-2 xl:order-none xl:w-auto xl:flex-1 xl:justify-end xl:border-0 xl:pt-0" : ["justify-end sm:order-none sm:w-auto", fullHeightList ? "sm:flex-1" : "sm:max-w-[45%]"])} />

          <div className="flex shrink-0 items-center gap-3 border-l-2 border-slate-300 pl-4">
             <div className="hidden md:block">
               <GlobalSearch />
             </div>

             {/* AI Search Bar */}
             <div className="hidden md:block">
               <AISearchBar />
             </div>

             <div className="relative" ref={alarmMenuRef}>
               <button
                 type="button"
                 aria-label={queryTraceRecording ? 'Oturum sorgu izleyiciyi aç, kayıt açık' : newAlarmCount > 0 ? `Alarm bildirimleri, son 2 saatte ${newAlarmCount} yeni aktif alarm` : 'Alarm bildirimleri'}
                 aria-expanded={queryTraceRecording ? queryTraceOpen : alarmMenuOpen}
                 aria-controls={queryTraceRecording ? 'session-query-trace-dialog' : 'header-alarm-menu'}
                 onClick={event => { if (queryTraceSupported && (event.shiftKey || queryTraceRecording)) { setAlarmMenuOpen(false); setQueryTraceOpen(true); } else setAlarmMenuOpen((prev) => !prev); }}
                 className={`relative h-10 w-10 flex items-center justify-center rounded-2xl transition-all ${
                   newAlarmCount > 0
                     ? 'bg-rose-50 text-rose-600 hover:bg-rose-100'
                     : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                 }`}
                 title={queryTraceRecording ? 'Sorgu kaydı açık · İzleyiciyi aç' : newAlarmCount > 0 ? `Son 2 saatte ${newAlarmCount} yeni aktif alarm` : 'Alarm bildirimi'}
               >
                 {queryTraceRecording && <span className="sr-only">Oturum sorgu kaydı açık</span>}
                 <Bell size={20} className={queryTraceRecording ? 'session-query-recording-icon text-red-600' : undefined} />
                 {newAlarmCount > 0 && (
                   <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                     {newAlarmCount > 99 ? '99+' : newAlarmCount}
                   </span>
                 )}
               </button>

               {alarmMenuOpen && (
                 <div id="header-alarm-menu" className="fixed right-4 top-20 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl z-50 sm:absolute sm:right-0 sm:top-auto">
                   {alarmLoadError && <p role="alert" className="mb-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">Alarm bilgileri güncellenemedi. Varsa son alınan değerler gösteriliyor.</p>}
                   {alarmSummary && (
                     <div className="mb-3 rounded-xl border border-slate-100 p-3">
                       <div className="flex items-center justify-between text-sm font-bold text-slate-900">
                         <span>Toplam aktif alarm</span><span>{alarmSummary.total.toLocaleString('tr-TR')}</span>
                       </div>
                       <div className="mt-1 text-[11px] text-slate-500">Tüm aktif santraller</div>
                       <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-semibold">
                         <span className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">Uyarı: {alarmSummary.warning}</span>
                         <span className="rounded-full bg-amber-50 px-2 py-1 text-amber-700">Minör: {alarmSummary.minor}</span>
                         <span className="rounded-full bg-rose-50 px-2 py-1 text-rose-700">Majör: {alarmSummary.major}</span>
                       </div>
                     </div>
                   )}
                   <div className="text-sm font-bold text-slate-900">
                     {newAlarmCount > 0 ? `${newAlarmCount} yeni aktif alarm` : alarmLoadError ? 'Yeni alarm bilgisi alınamadı' : 'Yeni alarm yok'}
                   </div>
                   <div className="mt-1 text-xs text-slate-500">
                     Son 2 saatte ilk kez bildirilen ve hala aktif alarmlar
                   </div>

                   {newAlarmRows.length > 0 && (
                     <div className="mt-3 max-h-44 space-y-2 overflow-y-auto pr-1">
                       {newAlarmRows.slice(0, 5).map((item, idx) => (
                         <div key={`${item.alarm_power_plant_name}-${item.alarm_inverter_name}-${item.alarm_code}-${idx}`} className="rounded-xl bg-slate-50 px-2 py-1.5">
                           <div className="text-[11px] font-semibold text-slate-700 truncate">{item.alarm_power_plant_name}</div>
                           <div className="text-[11px] text-slate-500 truncate">{item.alarm_inverter_name} • {item.alarm_code}</div>
                         </div>
                       ))}
                     </div>
                   )}

                   <button
                     type="button"
                     onClick={() => {
                       setAlarmMenuOpen(false);
                       navigate('/dashboard/alarms');
                     }}
                     className="mt-3 w-full rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800 transition"
                   >
                     Alarmlar Sayfasına Git
                   </button>
                 </div>
               )}
             </div>
             <div className="hidden xl:block"><PageHelp key={location.pathname} /></div>

          </div>
        </header>

        {/* Page content */}
        <main className={cn("min-h-0 flex-1 pt-2 px-5 overflow-y-auto custom-scrollbar", fullHeightList ? "pb-2" : "pb-5")}>
          <Outlet context={{ setPageTitle, headerActionsTarget } satisfies DashboardOutletContext} />
        </main>
      </div>
    </div>
  );
}

function SideNavLink({ to, icon, label, labelCls }: { to: string; icon: React.ReactNode; label: string; labelCls: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-bold transition-all duration-200 ${
          isActive 
            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
            : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-900'
        }`
      }
    >
      <span className="flex-shrink-0">{icon}</span>
      <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${labelCls}`}>
        {label}
      </span>
    </NavLink>
  );
}
